/**
 * Travel insurance sales for the Analytics panel (founder, 2026-10-23).
 *
 * Policies come from POST /api/admin/insurance-summary (every Faye policy with
 * how it was bought and whether its trip is ticketed); bookings are the page's
 * RAW rows, because the travel rows Analytics computes on have the premium
 * taken out (withoutInsurancePremium).
 *
 * Pure: no fetching, no React. The period is the page's own startDate/endDate,
 * compared as instants, so a month boundary means what the rest of the page
 * means by it (local time).
 */
import { FAYE_MIN_BASE_FARE } from '../../supabase/functions/_shared/faye-pricing.js';
import { insuranceAddedAfterBooking, insurancePremiumCents } from '../../supabase/functions/_shared/trip-insurance.js';

/** Faye went live at checkout (memory: faye-insurance-pricing). Nothing before
 *  this could have been offered, so it never counts against the take rate. */
export const FAYE_LAUNCHED_AT = new Date('2026-10-03T20:40:00Z');

const toMs = (v) => {
  if (v instanceof Date) return v.getTime();
  const ms = Date.parse(v ?? '');
  return Number.isFinite(ms) ? ms : null;
};
const fdOf = (b) => b?.flight_data || b?.flightData || {};
const createdMs = (b) => toMs(b?.created_at ?? b?.createdAt);
const statusOf = (b) => String(b?.status || b?.trip_status || 'pending').toLowerCase();

/**
 * Would checkout have offered Faye on this booking? The parts of
 * fayeOfferEligibility a stored booking can still show: Defera Capital, a
 * round trip, and a base fare of at least FAYE_MIN_BASE_FARE. Lead time,
 * birthdates and installments left can't be rebuilt afterwards, so this
 * slightly OVER-counts the eligible trips (the take rate errs low, never high).
 */
export function offeredAtCheckout(booking) {
  const fd = fdOf(booking);
  const method = fd._payment_method || fd.payment_method || booking?.payment_method;
  if (method !== 'defera_capital') return false;
  const slices = Array.isArray(fd.slices) ? fd.slices : [];
  if (slices.length < 2) return false;
  return Number(fd.total_amount) >= FAYE_MIN_BASE_FARE;
}

/** Bought with the booking: a premium in its totals and no "added later" marker. */
export function boughtAtCheckout(booking) {
  return insurancePremiumCents(booking) > 0 && insuranceAddedAfterBooking(booking) === null;
}

/**
 * Where a policy stands for ops. Defera buys the policy when it tickets the
 * trip, so an unbought policy on an unticketed trip is on schedule; on a
 * ticketed one it is overdue.
 */
export function policyStage(policy) {
  switch (policy?.status) {
    case 'pending':
      if (policy.trip_ended) return 'settling';
      return policy.ticketed ? 'buy_now' : 'at_ticketing';
    case 'issued': return 'issued';
    case 'needs_cancellation': return 'cancel_with_faye';
    case 'refund_due': return 'refund_due';
    case 'refunded': return 'refunded';
    case 'cancelled': return 'cancelled';
    default: return 'unknown';
  }
}

export const STAGE_LABELS = {
  buy_now: 'Buy now — ticketed',
  at_ticketing: 'Buy at ticketing',
  settling: 'Trip ended — settling',
  issued: 'Issued',
  cancel_with_faye: 'Cancel with Faye',
  refund_due: 'Refund due',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

const inRange = (ms, startMs, endMs) => ms != null && ms >= startMs && ms <= endMs;

/**
 * Everything the panel shows.
 *
 * - sold / premiums / plan mix / rows: policies BOUGHT in the period.
 * - take rate: of the bookings made in the period (from Faye's launch on)
 *   that checkout would have offered it to, how many bought it there.
 * - queue: every policy ops still has to buy, whatever the period — a work
 *   list, not a sales figure.
 */
export function summarizeInsuranceSales({ policies = [], bookings = [], startDate, endDate }) {
  const startMs = toMs(startDate) ?? -Infinity;
  const endMs = toMs(endDate) ?? Infinity;
  const list = Array.isArray(policies) ? policies : [];

  const inPeriod = list
    .filter((p) => inRange(toMs(p.created_at), startMs, endMs))
    .sort((a, b) => (toMs(b.created_at) ?? 0) - (toMs(a.created_at) ?? 0));

  const premiumCents = inPeriod.reduce((s, p) => s + (Number(p.premium_cents) || 0), 0);
  const atCheckout = inPeriod.filter((p) => p.how === 'checkout').length;
  const addedLater = inPeriod.length - atCheckout;

  const planMap = new Map();
  for (const p of inPeriod) {
    const label = p.plan_label || p.plan || 'Faye';
    const cur = planMap.get(label) || { label, count: 0, premiumCents: 0 };
    cur.count += 1;
    cur.premiumCents += Number(p.premium_cents) || 0;
    planMap.set(label, cur);
  }
  const plans = [...planMap.values()].sort((a, b) => b.count - a.count || b.premiumCents - a.premiumCents);

  // Take rate, over bookings made after launch and inside the period.
  const takeStartMs = Math.max(startMs, FAYE_LAUNCHED_AT.getTime());
  const madeInWindow = (Array.isArray(bookings) ? bookings : []).filter((b) => {
    const ms = createdMs(b);
    return inRange(ms, takeStartMs, endMs) && statusOf(b) !== 'pending';
  });
  const boughtThere = madeInWindow.filter(boughtAtCheckout);
  // A booking that bought it was offered it, whatever the reconstruction says.
  const eligible = madeInWindow.filter((b) => offeredAtCheckout(b) || boughtAtCheckout(b));
  const takeRate = {
    beforeLaunch: endMs < FAYE_LAUNCHED_AT.getTime(),
    bought: boughtThere.length,
    eligible: eligible.length,
    rate: eligible.length ? boughtThere.length / eligible.length : null,
  };

  // The ops queue: policies still to buy on live trips, soonest departure first.
  const toBuy = list
    .filter((p) => p.status === 'pending' && !p.trip_ended)
    .sort((a, b) => String(a.depart_date || '9999').localeCompare(String(b.depart_date || '9999')));
  const queue = {
    count: toBuy.length,
    buyNow: toBuy.filter((p) => p.ticketed).length,
    premiumCents: toBuy.reduce((s, p) => s + (Number(p.premium_cents) || 0), 0),
    nextDeparture: toBuy[0]?.depart_date || null,
    nextReference: toBuy[0]?.trip_reference || null,
  };

  return {
    sold: inPeriod.length,
    premiumCents,
    atCheckout,
    addedLater,
    plans,
    takeRate,
    queue,
    rows: inPeriod.map((p) => ({ ...p, stage: policyStage(p) })),
    allTimeSold: list.length,
  };
}
